import React from 'react';
import { TEACHER_RESOURCES, MODULES, DEFENCE_CHECKLIST } from '../constants';
import { ToggleLeft, ToggleRight, Printer, Clock, MessageCircle, FileText } from 'lucide-react';

interface TeacherResourcesProps {
  classModeEnabled: boolean;
  toggleClassMode: () => void;
}

export const TeacherResources: React.FC<TeacherResourcesProps> = ({ classModeEnabled, toggleClassMode }) => {
  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 print:hidden">
        <div>
          <h2 className="text-3xl font-bold text-cyber-dark">Teacher Resources</h2>
          <p className="text-slate-600">Lesson plans, printables, and classroom management tools.</p>
        </div>
        
        <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="text-right">
            <span className="block font-bold text-sm text-slate-700">Class Mode</span>
            <span className="block text-xs text-slate-500">Show timers & prompts</span>
          </div>
          <button onClick={toggleClassMode} className="text-cyber-accent hover:text-blue-700 transition-colors">
            {classModeEnabled ? <ToggleRight size={48} /> : <ToggleLeft size={48} className="text-slate-300" />}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 print:hidden">
        {/* Lesson Plan Column */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-xl shadow border-t-4 border-yellow-500">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Clock className="text-yellow-600" /> Lesson Plan (45-60 mins)
            </h3>
            
            <div className="space-y-6">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <span className="bg-yellow-200 text-yellow-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block">Starter • {TEACHER_RESOURCES.starter.duration}</span>
                <h4 className="font-bold text-lg mb-1">{TEACHER_RESOURCES.starter.title}</h4>
                <p className="text-slate-700 text-sm">{TEACHER_RESOURCES.starter.desc}</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block">Main • {TEACHER_RESOURCES.mainActivity.duration}</span>
                <h4 className="font-bold text-lg mb-1">{TEACHER_RESOURCES.mainActivity.title}</h4>
                <p className="text-slate-700 text-sm">{TEACHER_RESOURCES.mainActivity.desc}</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <span className="bg-green-200 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block">Plenary • {TEACHER_RESOURCES.plenary.duration}</span>
                <h4 className="font-bold text-lg mb-1">{TEACHER_RESOURCES.plenary.title}</h4>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-slate-700 text-sm">
                  {TEACHER_RESOURCES.plenary.questions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Downloads / Printables Column */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow border-t-4 border-cyber-dark">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="text-slate-700" /> Printables
            </h3>
            <button 
              onClick={() => window.print()}
              className="w-full flex items-center justify-center gap-2 bg-slate-800 text-white p-3 rounded-lg hover:bg-slate-900 transition-colors"
            >
              <Printer size={18} /> Print Cheat Sheet
            </button>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Scroll down to view the Cheat Sheet section below. This button prints the browser view (optimized for cheat sheet only).
            </p>
          </div>
        </div>
      </div>

      {/* Printable Cheat Sheet Section */}
      <div className="mt-16 bg-white border-2 border-slate-800 p-8 max-w-4xl mx-auto print:mt-0 print:border-0 print:p-0">
        <div className="text-center border-b-2 border-slate-800 pb-4 mb-6">
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Malware Defence Cheat Sheet</h1>
          <p className="text-slate-600">Quick Reference Guide for Students</p>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div>
            <h3 className="font-bold text-xl mb-4 bg-slate-900 text-white px-2 py-1 inline-block">Common Threats</h3>
            <div className="space-y-4">
              {MODULES.slice(0, 4).map(m => (
                <div key={m.id} className="border-l-4 border-slate-300 pl-3">
                  <h4 className="font-bold text-slate-800">{m.title}</h4>
                  <p className="text-sm text-slate-600 leading-tight">{m.definition}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-bold text-xl mb-4 bg-slate-900 text-white px-2 py-1 inline-block">Defensive Playbook</h3>
            <div className="space-y-3">
              {DEFENCE_CHECKLIST.map(d => (
                <div key={d.id} className="flex items-start gap-2">
                  <div className="bg-green-100 text-green-800 font-bold p-1 rounded text-xs min-w-[24px] text-center">✓</div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-800">{d.title}</h4>
                    <p className="text-xs text-slate-600">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 border-2 border-dashed border-slate-300 p-4 rounded bg-slate-50">
              <h4 className="font-bold text-slate-800 mb-2">Remember the CIA Triad</h4>
              <ul className="text-sm space-y-1">
                <li>🔒 <strong>Confidentiality:</strong> Only authorized people see data.</li>
                <li>🛡️ <strong>Integrity:</strong> Data is not tampered with.</li>
                <li>⚡ <strong>Availability:</strong> Data is accessible when needed.</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-center text-xs text-slate-400 print:fixed print:bottom-4 print:left-0 print:w-full">
          Malware Basics App • Educational Use Only
        </div>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          nav, footer {
            display: none;
          }
          /* Target the Cheat Sheet specifically */
          .max-w-4xl, .max-w-4xl * {
            visibility: visible;
          }
          .max-w-4xl {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 20px;
            border: none;
          }
        }
      `}</style>
    </div>
  );
};
